export const RECURRING_BONUS_TYPE = "recurring";
export const RELOAD_BONUS_TYPE = "reload";
export const CONFIRMATION_BONUS_TYPE = "confirmation";
export const BONUS_SCHEDULE_TIMEZONE = "UTC";

export const BONUS_CADENCES = ["daily", "weekly", "monthly"] as const;
export const BONUS_WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const BONUS_MONTH_DAYS = Array.from({ length: 28 }, (_, index) =>
  String(index + 1),
);

export const BONUS_SCHEDULE_FIELDS = [
  "recurringCadence",
  "recurringGrantTime",
  "recurringWeekday",
  "recurringDayOfMonth",
  "reloadStartWeekday",
  "reloadStartTime",
  "reloadEndWeekday",
  "reloadEndTime",
] as const;

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const CONFIRMATION_TOKENS = new Set(["email", "phone", "kyc"]);

export type BonusScheduleDraft = {
  typeData?: string;
  recurringCadence?: string;
  recurringGrantTime?: string;
  recurringWeekday?: string;
  recurringDayOfMonth?: string;
  reloadStartWeekday?: string;
  reloadStartTime?: string;
  reloadEndWeekday?: string;
  reloadEndTime?: string;
};

type BonusTypeOption = {
  id: number;
  title: string;
};

const isCadence = (value: string): value is (typeof BONUS_CADENCES)[number] =>
  (BONUS_CADENCES as readonly string[]).includes(value);

const isWeekday = (value: string): value is (typeof BONUS_WEEKDAYS)[number] =>
  (BONUS_WEEKDAYS as readonly string[]).includes(value);

const isTime = (value: string) => TIME_PATTERN.test(value);

const parseMonthDay = (value: unknown) => {
  const text = typeof value === "number" ? String(value) : textToken(value);
  if (!/^\d+$/.test(text)) return "";
  const day = Number(text);
  return day >= 1 && day <= 28 ? String(day) : "";
};

const parseTypeDataObject = (
  typeData: unknown,
): Record<string, unknown> | null => {
  if (typeof typeData !== "string" || !typeData.trim().startsWith("{")) {
    return null;
  }

  try {
    const parsed = JSON.parse(typeData);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};

const textToken = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export const findBonusTypeTitle = (
  typesList: BonusTypeOption[],
  typeId: unknown,
) => typesList.find((type) => String(type.id) === String(typeId))?.title;

export const bonusTypeDisplayLabel = (
  title: string,
  translate: (key: string) => string,
) => {
  if (title === RELOAD_BONUS_TYPE) return translate("reloadBonusType");
  if (title === RECURRING_BONUS_TYPE) return translate("recurringBonusType");
  return title;
};

export const hydrateBonusScheduleFields = (
  typeTitle: string | undefined,
  typeData: unknown,
): Pick<
  BonusScheduleDraft,
  | "recurringCadence"
  | "recurringGrantTime"
  | "recurringWeekday"
  | "recurringDayOfMonth"
  | "reloadStartWeekday"
  | "reloadStartTime"
  | "reloadEndWeekday"
  | "reloadEndTime"
> => {
  const parsed = parseTypeDataObject(typeData);

  if (typeTitle === RECURRING_BONUS_TYPE) {
    const cadence = textToken(parsed?.cadence);
    const grantTime = textToken(parsed?.grantTime);
    const explicitWeekday = textToken(parsed?.weekday);
    const explicitDay = parseMonthDay(parsed?.dayOfMonth);
    return {
      recurringCadence: isCadence(cadence) ? cadence : "",
      recurringGrantTime: isTime(grantTime) ? grantTime : "",
      recurringWeekday:
        cadence === "weekly"
          ? isWeekday(explicitWeekday)
            ? explicitWeekday
            : explicitWeekday
              ? ""
              : "monday"
          : "",
      recurringDayOfMonth:
        cadence === "monthly" ? explicitDay || (parsed?.dayOfMonth == null ? "1" : "") : "",
      reloadStartWeekday: "",
      reloadStartTime: "",
      reloadEndWeekday: "",
      reloadEndTime: "",
    };
  }

  if (typeTitle === RELOAD_BONUS_TYPE) {
    const startWeekday = textToken(parsed?.startWeekday);
    const startTime = textToken(parsed?.startTime);
    const endWeekday = textToken(parsed?.endWeekday);
    const endTime = textToken(parsed?.endTime);
    return {
      recurringCadence: "",
      recurringGrantTime: "",
      recurringWeekday: "",
      recurringDayOfMonth: "",
      reloadStartWeekday: isWeekday(startWeekday) ? startWeekday : "",
      reloadStartTime: isTime(startTime) ? startTime : "",
      reloadEndWeekday: isWeekday(endWeekday) ? endWeekday : "",
      reloadEndTime: isTime(endTime) ? endTime : "",
    };
  }

  return {
    recurringCadence: "",
    recurringGrantTime: "",
    recurringWeekday: "",
    recurringDayOfMonth: "",
    reloadStartWeekday: "",
    reloadStartTime: "",
    reloadEndWeekday: "",
    reloadEndTime: "",
  };
};

/**
 * Serialize only the selected type's approved typeData.
 * Hidden draft fields from another type are ignored.
 */
export const serializeBonusTypeData = (
  typeTitle: string | undefined,
  payload: BonusScheduleDraft,
): string | undefined => {
  if (typeTitle === RECURRING_BONUS_TYPE) {
    const cadence = textToken(payload.recurringCadence);
    const grantTime = textToken(payload.recurringGrantTime);
    if (!isCadence(cadence) || !isTime(grantTime)) return undefined;
    if (cadence === "weekly") {
      const weekday = textToken(payload.recurringWeekday);
      if (!isWeekday(weekday)) return undefined;
      return JSON.stringify({ cadence, grantTime, weekday });
    }
    if (cadence === "monthly") {
      const dayOfMonth = Number(parseMonthDay(payload.recurringDayOfMonth));
      if (!dayOfMonth) return undefined;
      return JSON.stringify({ cadence, grantTime, dayOfMonth });
    }
    return JSON.stringify({ cadence, grantTime });
  }

  if (typeTitle === RELOAD_BONUS_TYPE) {
    const startWeekday = textToken(payload.reloadStartWeekday);
    const startTime = textToken(payload.reloadStartTime);
    const endWeekday = textToken(payload.reloadEndWeekday);
    const endTime = textToken(payload.reloadEndTime);
    if (
      !isWeekday(startWeekday) ||
      !isTime(startTime) ||
      !isWeekday(endWeekday) ||
      !isTime(endTime)
    ) {
      return undefined;
    }
    return JSON.stringify({
      startWeekday,
      startTime,
      endWeekday,
      endTime,
    });
  }

  if (
    typeTitle === CONFIRMATION_BONUS_TYPE &&
    payload.typeData &&
    CONFIRMATION_TOKENS.has(payload.typeData)
  ) {
    return payload.typeData;
  }

  return undefined;
};

export const bonusScheduleFieldErrors = (
  typeTitle: string | undefined,
  payload: BonusScheduleDraft,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (typeTitle === RECURRING_BONUS_TYPE) {
    const cadence = textToken(payload.recurringCadence);
    if (!isCadence(cadence)) {
      errors.recurringCadence = "requiredField";
    } else if (cadence === "weekly" && !isWeekday(textToken(payload.recurringWeekday))) {
      errors.recurringWeekday = "requiredField";
    } else if (cadence === "monthly" && !parseMonthDay(payload.recurringDayOfMonth)) {
      errors.recurringDayOfMonth = "requiredField";
    }
    if (!isTime(textToken(payload.recurringGrantTime))) {
      errors.recurringGrantTime = "requiredField";
    }
  }

  if (typeTitle === RELOAD_BONUS_TYPE) {
    if (!isWeekday(textToken(payload.reloadStartWeekday))) {
      errors.reloadStartWeekday = "requiredField";
    }
    if (!isTime(textToken(payload.reloadStartTime))) {
      errors.reloadStartTime = "requiredField";
    }
    if (!isWeekday(textToken(payload.reloadEndWeekday))) {
      errors.reloadEndWeekday = "requiredField";
    }
    if (!isTime(textToken(payload.reloadEndTime))) {
      errors.reloadEndTime = "requiredField";
    }
  }

  return errors;
};
